"use client";
import { faker } from "@faker-js/faker";
import { Prisma, SponsorshipPackageTier } from "@prisma/client";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { env } from "process";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { SponsorStep2 } from "~/app/events/[slug]/sponsor/_components/SponsorStep2";
import { SponsorStep3 } from "~/app/events/[slug]/sponsor/_components/SponsorStep3";
import SponsorTierSelector from "~/app/events/[slug]/sponsor/_components/SponsorTierSelector";
import { ApplyToSponsorFormData } from "~/app/events/[slug]/sponsor/_components/types";
import { toastHandler } from "~/components/ToastHandler";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "~/components/v3/shadcn/Card";
import { Button } from "~/components/v3/themed/Button";
import LoginModal from "~/components/v3/themed/LoginModal";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { api } from "~/utils/api";
import { applyToSponsorCommunity, EventAndCommunity } from "../actions";
import FloatingSponsorIndicator from "./FloatingSponsorIndicator";
import SearchableTable from "./SearchableTable";

interface ServerSearchOptions {
    communityTags: {
        defaultTags: PrismaJson.DefaultTags[];
    } | null;
}

interface Props {
    sponsorshipPackage: Prisma.SponsorshipPackageGetPayload<{
        include: { packageTiers: true };
    }>;
    communitySlug: string;
    events: EventAndCommunity[];
    searchOptions: ServerSearchOptions | null;
    onSubmit?: (data: {
        selectedTier: SponsorshipPackageTier;
        selectedEvents: EventAndCommunity[];
        formData: ApplyToSponsorFormData;
    }) => void;
}

const SponsorCommunity = ({
    sponsorshipPackage,
    communitySlug,
    events,
    searchOptions,
    onSubmit,
}: Props) => {
    const apiCtx = api.useUtils();
    const router = useRouter();
    const { data: session } = useSession();

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedTier, setSelectedTier] =
        useState<SponsorshipPackageTier | null>(null);
    const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
        new Set(),
    );
    const [selectedEvents, setSelectedEvents] = useState<EventBySlugOutput[]>(
        [],
    );
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);

    const testModeDefaultValues = {
        name: faker.name.fullName(),
        email: session?.user?.email ?? faker.internet.email(),
        description: faker.lorem.sentence(),
        image: faker.image.avatar(),
        selectedPackageTier: undefined,
        socialLinks: {
            x: "andrew_pynch",
        },
        url: faker.internet.url(),
    };
    const defaultValues =
        env.NODE_END === "production"
            ? testModeDefaultValues
            : {
                  name: "",
                  email: "",
                  description: "",
                  image: "",
                  selectedPackageTier: undefined,
                  socialLinks: {},
              };
    const form = useForm<ApplyToSponsorFormData>({
        defaultValues: defaultValues,
    });

    useEffect(() => {
        const fetchSelectedEvents = async () => {
            if (selectedEventIds.size === 0) {
                setSelectedEvents([]);
                return;
            }

            setIsLoadingEvents(true);
            try {
                const eventPromises = Array.from(selectedEventIds).map((id) => {
                    const event = events.find((e) => e.id === id);
                    if (!event) return null;
                    return apiCtx.event.getBySlug.fetch(event.slug);
                });

                const fetchedEvents = await Promise.all(eventPromises);
                setSelectedEvents(
                    fetchedEvents.filter(
                        (e): e is EventBySlugOutput => e !== null,
                    ),
                );
            } catch (error) {
                console.error("Error fetching events:", error);
            } finally {
                setIsLoadingEvents(false);
            }
        };

        fetchSelectedEvents();
    }, [selectedEventIds]);

    const handleNext = () => {
        if (currentStep === 0) {
            if (!selectedTier) return;
            form.setValue("selectedPackageTier", selectedTier);

            const selectedEvents = events.filter((event) =>
                selectedEventIds.has(event.id),
            );
            if (selectedEvents.length === 0) return;

            setCurrentStep(1);
        } else if (currentStep === 1) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        if (!selectedTier) {
            toast.error("Please select a sponsorship tier");
            return;
        }

        const values = form.getValues();
        const valuesWithCasting = {
            ...values,
            selectedPackageTier: {
                ...values.selectedPackageTier,
                customAttendees: values.selectedPackageTier
                    ?.customAttendees as PrismaJson.CustomAttendees,
            },
        };

        await toastHandler({
            loadingMessage: "Submitting sponsorship application...",
            successMessage: "Sponsorship application submitted successfully!",
            errorMessage:
                "Failed to submit sponsorship application. Please try again.",
            operation: async () => {
                const result = await applyToSponsorCommunity({
                    selectedTier,
                    selectedEvents,
                    formData: valuesWithCasting,
                });

                // Return the result first
                return result;
            },
            onSuccess: (result) => {
                // Handle redirect in the success callback
                if (result?.application?.id) {
                    window.open(
                        `/community-management/${communitySlug}/sponsors/applications/${result.application.id}`,
                        "_blank",
                    );
                }
            },
            onError: (error) => {
                console.error(
                    "Error submitting sponsorship application:",
                    error,
                );
            },
        });
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="flex w-full flex-col">
                        <div className="pt-6">
                            <SponsorTierSelector
                                sponsorshipPackage={sponsorshipPackage}
                                communitySlug={communitySlug}
                                showTitle={true}
                                onTierSelect={setSelectedTier}
                                selectedTier={selectedTier}
                            />
                        </div>
                        <div className="pt-6">
                            <SearchableTable
                                events={events}
                                searchOptions={searchOptions?.communityTags}
                                showBorder={false}
                                showTagsFilter={false}
                                selectable={true}
                                selectedIds={selectedEventIds}
                                selectedTier={selectedTier}
                                onSelectionChange={setSelectedEventIds}
                            />
                        </div>
                    </div>
                );
            case 1:
                return <SponsorStep2 />;
            case 2:
                return isLoadingEvents ? (
                    <div>Loading...</div>
                ) : (
                    <SponsorStep3 events={selectedEvents} />
                );
            default:
                return null;
        }
    };

    if (!session) {
        return (
            <Card className="mx-auto mt-8 max-w-2xl">
                <CardHeader>
                    <CardTitle>Log in to Apply to Sponsor</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-4 text-v3-N-300">
                        You must be logged in to apply to a sponsor for this
                        event.
                    </p>
                    <Button
                        variant="default"
                        onClick={() => setShowLoginModal(true)}
                    >
                        Continue
                    </Button>

                    <LoginModal
                        title="Sign Up or Log in to Apply to Sponsor"
                        isOpen={showLoginModal}
                        setIsOpen={setShowLoginModal}
                        description="Sign Up to Apply to Sponsor Events on River!"
                    />
                </CardContent>
            </Card>
        );
    }

    return (
        <FormProvider {...form}>
            <div className="mt-10 flex w-[95vw] flex-col md:mt-0 md:w-[70vw]">
                {renderStep()}
                <FloatingSponsorIndicator
                    currentStep={currentStep}
                    selectedTier={selectedTier}
                    selectedEventIds={selectedEventIds}
                    onNext={handleNext}
                    onBack={currentStep > 0 ? handleBack : undefined}
                    nextLabel={
                        currentStep === 0
                            ? "Continue"
                            : currentStep === 2
                              ? "Submit"
                              : currentStep === 3
                                ? "Submit payment hold"
                                : "Next"
                    }
                />
            </div>
        </FormProvider>
    );
};

export default SponsorCommunity;
